import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Package, Calendar, Truck, Pause, Play, XCircle, Loader2,
  AlertCircle, CheckCircle, Clock, MapPin
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  paused: 'bg-amber-100 text-amber-700 border-amber-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
};

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function ActiveSubscriptions() {
  const [params] = useSearchParams();
  const { toast } = useToast();
  const token = params.get('token');
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [err, setErr] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirm, setConfirm] = useState(null); // { id, type }
  const [reason, setReason] = useState('');

  const api = useCallback(async (path, opts = {}) => {
    const url = `${API_BASE}${path}${path.includes('?') ? '&' : '?'}token=${encodeURIComponent(token || '')}`;
    const res = await fetch(url, {
      ...opts,
      headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || 'Request failed');
    return data;
  }, [token]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const data = await api('/api/customer-portal/subscriptions');
      setSubs(data.subscriptions || []);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { if (token) load(); else { setErr('Missing access token in URL'); setLoading(false); } }, [token, load]);

  const doAction = async (id, type, body) => {
    setActionLoading(`${id}-${type}`);
    try {
      const result = await api(`/api/customer-portal/subscriptions/${id}/${type}`, {
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      });
      const msg = type === 'cancel'
        ? `Cancelled. Prorated refund: Rs.${result.prorated_refund?.toFixed(2) || 0}`
        : type === 'pause' ? 'Subscription paused. Billing halted.'
        : type === 'resume' ? `Resumed. Next billing: ${new Date(result.new_next_billing_date).toLocaleDateString()}`
        : 'Updated successfully';
      toast({ title: 'Success', description: msg });
      setConfirm(null); setReason('');
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
    </div>
  );

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="max-w-md">
        <CardContent className="p-6 text-center space-y-3">
          <AlertCircle className="h-10 w-10 text-red-500 mx-auto" />
          <h2 className="font-semibold text-gray-900">Unable to load subscriptions</h2>
          <p className="text-sm text-gray-600">{err}</p>
          <p className="text-xs text-gray-400">Magic links expire after 7 days — check your latest email.</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="customer-portal">
      <div className="max-w-3xl mx-auto space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Subscriptions</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your active subscriptions, pause deliveries when travelling, or cancel anytime.</p>
        </div>

        {subs.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <Package className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium text-gray-600">No active subscriptions</p>
            </CardContent>
          </Card>
        ) : (
          subs.map(sub => (
            <Card key={sub.id} data-testid={`sub-${sub.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <CardTitle className="text-lg">{sub.listing_title}</CardTitle>
                    <CardDescription className="flex items-center gap-2 mt-1">
                      <Badge className={`text-[10px] px-2 border ${STATUS_COLORS[sub.status] || 'bg-gray-100'}`}>{sub.status}</Badge>
                      <span>Order {sub.shopify_order_number}</span>
                    </CardDescription>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold">Rs.{sub.billing_amount?.toFixed(0)}</p>
                    <p className="text-xs text-gray-500">/{sub.billing_cycle}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-0 space-y-3">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Calendar className="h-3.5 w-3.5" /> {sub.billing_cycle} billing
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Truck className="h-3.5 w-3.5" /> {sub.delivery_frequency} delivery
                  </div>
                  {sub.next_billing_date && (
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <Clock className="h-3.5 w-3.5" /> Next: {new Date(sub.next_billing_date).toLocaleDateString()}
                    </div>
                  )}
                  {sub.shipping_address?.city && (
                    <div className="flex items-center gap-1.5 text-gray-600 truncate">
                      <MapPin className="h-3.5 w-3.5" /> {sub.shipping_address.city}
                    </div>
                  )}
                </div>

                {sub.status === 'paused' && sub.pre_pause_next_billing_date && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 text-xs text-amber-800">
                    <strong>Paused.</strong> When you resume, your next billing will shift forward by however long you remain paused — you won't lose any deliveries you've paid for.
                  </div>
                )}

                {confirm?.id === sub.id && confirm?.type === 'cancel' && (
                  <div className="space-y-2 border-t pt-3">
                    <p className="text-sm text-red-700"><strong>Confirm cancellation.</strong> Prorated refund will be calculated.</p>
                    <Textarea
                      placeholder="Reason for cancellation (optional)"
                      value={reason}
                      onChange={e => setReason(e.target.value)}
                      className="text-sm"
                      data-testid="cancel-reason"
                    />
                    <div className="flex gap-2">
                      <Button size="sm" variant="destructive" onClick={() => doAction(sub.id, 'cancel', { reason })}
                        disabled={actionLoading === `${sub.id}-cancel`} data-testid="confirm-cancel">
                        {actionLoading === `${sub.id}-cancel` ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Confirm Cancel'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setConfirm(null); setReason(''); }}>
                        Keep subscription
                      </Button>
                    </div>
                  </div>
                )}

                {!confirm && sub.status === 'active' && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Button size="sm" variant="outline" onClick={() => doAction(sub.id, 'pause')}
                      disabled={actionLoading === `${sub.id}-pause`} className="text-amber-700 border-amber-200"
                      data-testid={`pause-${sub.id}`}>
                      {actionLoading === `${sub.id}-pause` ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Pause className="h-3 w-3 mr-1" />}
                      Pause
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setConfirm({ id: sub.id, type: 'cancel' })}
                      className="text-red-700 border-red-200" data-testid={`cancel-${sub.id}`}>
                      <XCircle className="h-3 w-3 mr-1" /> Cancel
                    </Button>
                  </div>
                )}

                {!confirm && sub.status === 'paused' && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Button size="sm" variant="outline" onClick={() => doAction(sub.id, 'resume')}
                      disabled={actionLoading === `${sub.id}-resume`} className="text-emerald-700 border-emerald-200"
                      data-testid={`resume-${sub.id}`}>
                      {actionLoading === `${sub.id}-resume` ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Play className="h-3 w-3 mr-1" />}
                      Resume Deliveries
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setConfirm({ id: sub.id, type: 'cancel' })}
                      className="text-red-700 border-red-200">
                      <XCircle className="h-3 w-3 mr-1" /> Cancel Instead
                    </Button>
                  </div>
                )}

                {sub.status === 'cancelled' && (
                  <div className="pt-2 border-t flex items-center gap-2 text-xs text-gray-500">
                    <CheckCircle className="h-3.5 w-3.5 text-gray-400" />
                    Cancelled{sub.cancelled_at && ` on ${new Date(sub.cancelled_at).toLocaleDateString()}`}
                    {sub.prorated_refund > 0 && ` — refund Rs.${sub.prorated_refund.toFixed(2)}`}
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}

        <p className="text-center text-xs text-gray-400 pt-4">
          Powered by Aniyor. For questions, reply to your vendor's order confirmation email.
        </p>
      </div>
    </div>
  );
}
